import { Location, NgIf, NgFor, NgClass, AsyncPipe, LowerCasePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
  MatError,
  MatPrefix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { PageDetailComponent } from '@next/core-lib/components';
import { AgrConnectFormDirective, AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, CurrencySymbolPipe, TranslatePipe } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { select, Store } from '@ngrx/store';
import { merge, Observable, Subject } from 'rxjs';
import { map, shareReplay, skipWhile, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { VatPercentages } from 'src/app/shared/constants';
import Utils from 'src/app/shared/utils';

import { Barn, Cycle, DayOldChick, Departure, Slaughterhouse } from '../../models/cycle.model';
import { FinalSetupNavigationService } from '../../services/final-setup-navigation.service';
import * as cycleActions from '../../store/actions/cycle.actions';
import {
  getCycles,
  getSelectedCycle,
  getSlaughterhouses,
} from '../../store/selectors/cycle.selectors';
import { getBarnsInUse, getDayOldChicks } from '../../store/selectors/day-old-chick.selectors';
import {
  getNextDepartureId,
  getSelectedDeparture,
} from '../../store/selectors/departure.selectors';
import { departureAmountValidator } from '../../validators/departure-amount-validator';

@Component({
  selector: 'app-departure-item',
  templateUrl: 'departure-item.component.html',
  styleUrls: ['departure-item.component.scss'],
  imports: [
    PageDetailComponent,
    NgIf,
    ReactiveFormsModule,
    AgrConnectFormDirective,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatError,
    MatSelect,
    NgFor,
    MatOption,
    AgrNumberDirective,
    MatSlideToggle,
    NgClass,
    MatPrefix,
    MatIcon,
    MatButton,
    AsyncPipe,
    LowerCasePipe,
    CurrencySymbolPipe,
    TranslatePipe,
  ],
})
export class DepartureItemComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly locale = inject(LocaleService);
  private readonly location = inject(Location);
  private finalSetupNavigationService = inject(FinalSetupNavigationService);

  routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getPath));

  pageTitle$: Observable<string> = this.store.pipe(
    withLatestFrom(this.routeUrl$),
    map(([_, url]) =>
      url.endsWith('/departure/add')
        ? this.locale.translate('add') + ' ' + this.locale.translate('cycle-navigation.departure')
        : this.locale.translate('edit-departure'),
    ),
  );

  barn = new FormControl(null, [Validators.required]);

  barns$: Observable<Barn[]> = this.store.select(getBarnsInUse);
  slaughterhouses$: Observable<Slaughterhouse[]> = this.store.select(getSlaughterhouses);
  cycle$: Observable<Cycle> = this.store.select(getSelectedCycle);
  departure$: Observable<Departure> = this.store.select(getSelectedDeparture);
  nextDepartureId$: Observable<number> = this.store.select(getNextDepartureId);
  departureNotNull$: Observable<Departure> = this.departure$.pipe(
    skipWhile((departure) => !departure),
    shareReplay(1),
    take(1),
    map((o) => ({
      ...o,
      revenue: o.revenue ?? null,
      amounts: o.revenue != null,
    })),
  );
  minArrivalDate$: Observable<Date> = this.store.select(getDayOldChicks).pipe(
    take(1),
    map(
      (dayOldChicks: DayOldChick[]) =>
        new Date(
          dayOldChicks
            .map((chick) => chick.arrivalDate)
            .slice()
            .sort((a, b) => (a > b ? 1 : -1))[0],
        ),
    ),
  );
  maxArrivalDate = new Date();

  departureForm = new FormGroup(
    {
      id: new FormControl(null, []),
      barn: this.barn,
      type: new FormControl(null, [Validators.required]),
      slaughterhouse: new FormControl(null, []),
      amountOfChicks: new FormControl(
        null,
        [Validators.required, Validators.min(1)],
        departureAmountValidator(this.store, this.barn),
      ),
      departureDate: new FormControl(null, [Validators.required]),
      footpadLesionScore: new FormControl<number>(null, []),
      footpadLesionNonePercentage: new FormControl<number>(null, []),
      footpadLesionModeratePercentage: new FormControl<number>(null, []),
      footpadLesionSeverePercentage: new FormControl<number>(null, []),
      heelDermatitis: new FormControl(null, []),
      departureWeight: new FormControl<number>(null, []),
      rejectionAtSlaughterhouseInKg: new FormControl<number>(null, []),
      rejectionDuringTransportInKg: new FormControl<number>(null, []),
      slaughterWeight: new FormControl<number>(undefined, []),
      amounts: new FormControl<boolean>(true, []),
      revenueSubTotal: new FormControl<number>(null, []),
      diverseCosts: new FormControl<number>(null, []),
      revenue: new FormControl<number>(null, []),
      invoiceDate: new FormControl<string>(null, []),
      invoiceNumber: new FormControl<string>(null, [Validators.maxLength(50)]),
      vatPercentage: new FormControl<number>(null, []),
      showInFinalSetup: new FormControl<boolean>(null, []),
    },
    {
      validators: [
        this.validateFootpadLesionPercentages(
          'footpadLesionNonePercentage',
          'footpadLesionModeratePercentage',
          'footpadLesionSeverePercentage',
        ),
        this.validateWeightvalues('departureWeight', 'slaughterWeight'),
      ],
    },
  );
  unsubscribeSignal: Subject<void> = new Subject<void>();
  isFinalSetup$: Observable<boolean> = this.finalSetupNavigationService.canNavigate();
  vatPercentages = VatPercentages;
  isFinalSetup: boolean;

  validateWeightvalues(departureKey: string, slaughterKey: string): ValidatorFn {
    return (group: FormGroup): { [key: string]: unknown } => {
      const departure = group.controls[departureKey];
      const slaughter = group.controls[slaughterKey];

      if (departure.value === null && slaughter.value === null) {
        return null;
      } else if (Number(slaughter.value) > Number(departure.value)) {
        return { slaughterWeightHigherThanDepartureWeight: true };
      }
    };
  }

  validateFootpadLesionPercentages(
    noneKey: string,
    moderateKey: string,
    severeKey: string,
  ): ValidatorFn {
    return (group: FormGroup): { [key: string]: unknown } => {
      const none = group.controls[noneKey];
      const moderate = group.controls[moderateKey];
      const severe = group.controls[severeKey];

      if (none.value === null && moderate.value === null && severe.value === null) {
        return null;
      } else if (
        Math.round((Number(none.value) + Number(moderate.value) + Number(severe.value)) * 100) /
          100 !==
        100
      ) {
        return { mustBe100percent: true };
      }
    };
  }

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.isFinalSetup$
      .pipe(take(1))
      .subscribe((isFinalSetup) => (this.isFinalSetup = isFinalSetup));
  }

  ngOnInit() {
    merge(
      this.departureForm.controls.departureWeight.valueChanges,
      this.departureForm.controls.rejectionAtSlaughterhouseInKg.valueChanges,
      this.departureForm.controls.rejectionDuringTransportInKg.valueChanges,
    )
      .pipe(takeUntil(this.unsubscribeSignal))
      .subscribe(() =>
        this.departureForm.controls.slaughterWeight.patchValue(
          this.departureForm.controls.departureWeight.value -
            this.departureForm.controls.rejectionAtSlaughterhouseInKg.value -
            this.departureForm.controls.rejectionDuringTransportInKg.value,
        ),
      );

    merge(
      this.departureForm.controls.revenueSubTotal.valueChanges,
      this.departureForm.controls.diverseCosts.valueChanges,
    )
      .pipe(takeUntil(this.unsubscribeSignal))
      .subscribe(() =>
        this.departureForm.controls.revenue.patchValue(
          this.departureForm.controls.revenueSubTotal.value -
            this.departureForm.controls.diverseCosts.value,
        ),
      );

    this.departureForm.controls.amounts.valueChanges
      .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
      .subscribe((amounts: boolean) => {
        if (!amounts) {
          this.departureForm.controls.revenueSubTotal.patchValue(null);
          this.departureForm.controls.diverseCosts.patchValue(null);
          this.departureForm.controls.revenue.patchValue(null);
          this.departureForm.controls.revenue.clearValidators();
          this.departureForm.controls.revenue.updateValueAndValidity();
        } else {
          this.departureForm.controls.revenue.setValidators(Validators.required);
          this.departureForm.controls.revenue.updateValueAndValidity();
        }
      });

    merge(
      this.departureForm.controls.footpadLesionModeratePercentage.valueChanges,
      this.departureForm.controls.footpadLesionSeverePercentage.valueChanges,
    )
      .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
      .subscribe(() =>
        this.departureForm.controls.footpadLesionScore.patchValue(
          this.departureForm.controls.footpadLesionModeratePercentage.value * 0.5 +
            this.departureForm.controls.footpadLesionSeverePercentage.value * 2,
        ),
      );

    // amountOfCHicks-validation must run again when the barn is changed
    this.departureForm.controls.barn.valueChanges
      .pipe(takeUntil(this.unsubscribeSignal.asObservable()))
      .subscribe((_) => {
        this.departureForm.controls.amountOfChicks.updateValueAndValidity();
      });

    this.barns$
      .pipe(
        withLatestFrom(this.routeUrl$),
        skipWhile(([barns, _url]: [Barn[], string]) => barns === undefined),
        take(1),
      )
      .subscribe(([barns, url]: [Barn[], string]) => {
        if (url.endsWith('/departure/add')) {
          if (barns.length > 0) {
            this.departureForm.controls.barn.patchValue(barns[0]);
          }
        }
      });
    this.store
      .select(getCycles)
      .pipe(take(1))
      .subscribe((cycles: Cycle[]) => {
        const lastCyclesWithSlaughterhouse = cycles
          .map((cycle) => {
            if (
              !!cycle.departures &&
              cycle.departures.some(
                (departure) => departure.slaughterhouse !== null && departure.barn !== null,
              )
            ) {
              return cycle;
            }
          })
          .slice()
          .sort((a, b) => (a.cycleDate < b.cycleDate ? 1 : -1));

        const lastCycleWithSlaughterhouse =
          lastCyclesWithSlaughterhouse.length > 0 ? lastCyclesWithSlaughterhouse[0] : undefined;
        if (lastCycleWithSlaughterhouse) {
          this.departureForm.controls.slaughterhouse.patchValue(
            lastCycleWithSlaughterhouse.departures[0].slaughterhouse,
          );
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribeSignal.next();
    this.unsubscribeSignal.unsubscribe();
  }

  back() {
    if (this.router.navigated) {
      this.location.back();
    } else {
      this.router.navigate(['../'], { relativeTo: this.activatedRoute });
    }
  }

  compareBarns = (b1: Barn, b2: Barn) => b1 && b2 && b1.id === b2.id;
  compareSlaughterhouses = (s1: Slaughterhouse, s2: Slaughterhouse) => s1 && s2 && s1.id === s2.id;

  save() {
    if (this.departureForm.valid) {
      this.cycle$
        .pipe(take(1), withLatestFrom(this.departure$, this.nextDepartureId$))
        .subscribe(([cycle, departure, nextId]) => {
          const formValue = {
            ...this.departureForm.value,
            barn: this.departureForm.value.barn,
            type: this.departureForm.value.type,
            departureDate: Utils.parseDateTimeFromFormValue(
              this.departureForm.controls.departureDate.value,
            ),
            amountOfChicks: <number>this.departureForm.controls.amountOfChicks.value,
            invoiceDate: Utils.parseDateTimeFromFormValue(
              this.departureForm.controls.invoiceDate.value,
            ),
            invoiceNumber: this.departureForm.controls.invoiceNumber.value,
            vatPercentage: this.departureForm.controls.vatPercentage.value,
          };
          if (departure) {
            this.store.dispatch(
              cycleActions.editDeparture({
                cycleId: cycle.id,
                departure: { ...formValue, id: departure.id },
              }),
            );
          } else {
            this.store.dispatch(
              cycleActions.addDeparture({
                cycleId: cycle.id,
                departure: { ...formValue, id: nextId },
              }),
            );
          }
        });
    }
  }
}
